import { c as _c } from "react/compiler-runtime";
import { buildSessionsScopeParam } from "@/lib/assistant-sessions";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useDebouncedValue } from "./useDebouncedValue";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { orgInstanceSlug, useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import type { CachedAssistantSession } from "./useAssistantSessionCache";
export interface SemanticSessionMatch extends CachedAssistantSession {
  similarity: number;
  snippet: string;
}
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const STALE_TIME = 60 * 1000; // 1 minute

// Semantic search over embedded Codesmith session transcripts, powering
// the "by meaning" session matches in the command palette. Complements the
// client-side title-substring search in useAssistantSessionCache.
export function useSemanticSessionSearch(searchTerm) {
  const $ = _c(26);
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== connectedGithubOrgs?.installations) {
    t0 = connectedGithubOrgs?.installations.find(installation => installation?.account?.login === activeGithubOrg)?.codesmith_enabled ?? false;
    $[0] = activeGithubOrg;
    $[1] = connectedGithubOrgs?.installations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const codesmithEnabled = t0;
  const t1 = activeGithubOrg ?? null;
  let t2;
  if ($[3] !== connectedGithubOrgs || $[4] !== t1) {
    t2 = orgInstanceSlug(connectedGithubOrgs, t1);
    $[3] = connectedGithubOrgs;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const instanceSlug = t2;
  let t3;
  if ($[6] !== searchTerm) {
    t3 = searchTerm.trim();
    $[6] = searchTerm;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const trimmed = t3;
  const debouncedTerm = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const searchable = !!activeGithubOrg && !!instanceSlug && codesmithEnabled;
  const enabled = searchable && debouncedTerm.length >= MIN_QUERY_LENGTH;
  let t4;
  if ($[8] !== activeGithubOrg || $[9] !== debouncedTerm || $[10] !== instanceSlug) {
    t4 = ["semanticSessionSearch", instanceSlug, activeGithubOrg, debouncedTerm];
    $[8] = activeGithubOrg;
    $[9] = debouncedTerm;
    $[10] = instanceSlug;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== activeGithubOrg || $[13] !== api || $[14] !== debouncedTerm || $[15] !== instanceSlug) {
    t5 = async () => {
      const response = await api.get(`/assistant/sessions/semantic-search?q=${encodeURIComponent(debouncedTerm)}${buildSessionsScopeParam(null, {
        orgName: activeGithubOrg,
        instanceSlug
      })}`);
      return (response.data.sessions || []) as SemanticSessionMatch[];
    };
    $[12] = activeGithubOrg;
    $[13] = api;
    $[14] = debouncedTerm;
    $[15] = instanceSlug;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== enabled) {
    t6 = {
      enabled,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false
    };
    $[17] = enabled;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const {
    data,
    isLoading
  } = useQuery(t4, t5, t6);
  const isDebouncing = searchable && trimmed.length >= MIN_QUERY_LENGTH && trimmed !== debouncedTerm;
  let t7;
  if ($[19] !== data || $[20] !== enabled || $[21] !== isDebouncing) {
    t7 = enabled && !isDebouncing ? data ?? [] : [];
    $[19] = data;
    $[20] = enabled;
    $[21] = isDebouncing;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = enabled && isLoading || isDebouncing;
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = {
      sessions: t7,
      isLoading: t8
    };
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}